import { aiserverbaselink } from '../../../Env_Variables';
import { executeDashboardTool } from './aiTools';
import { aiInstituteHeaders } from './aiInstituteHeaders';

const MUTATING_TOOLS = new Set([
    'create_customer',
    'create_collection',
    'import_catalog_groups',
    'generate_products_import_excel',
    'update_product_info',
    'update_product_price',
    'update_product_quantity',
    'bulk_update_products',
    'apply_product_discount',
    'add_city_to_zone',
]);

function parseSseBlock(chunk) {
    let event = 'message';
    const dataLines = [];
    for (const line of String(chunk || '').split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) return null;
    let data = dataLines.join('\n');
    try {
        data = JSON.parse(data);
    } catch (_) {
        /* keep string */
    }
    return { event, data };
}

async function consumeSse(response, onEvent, signal) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const chunk of parts) {
            const ev = parseSseBlock(chunk);
            if (ev) onEvent(ev);
        }
    }
    if (buffer.trim()) {
        const ev = parseSseBlock(buffer);
        if (ev) onEvent(ev);
    }
}

function safeJson(text) {
    try {
        return JSON.parse(text || '{}');
    } catch (_) {
        return {};
    }
}

function emptyUsage() {
    return {
        prompt_tokens: 0,
        completion_tokens: 0,
        cached_tokens: 0,
        reasoning_tokens: 0,
        total_tokens: 0,
        cost_usd: 0,
        cost_usd_display: '$0.000000',
        model: null,
    };
}

function mergeUsage(a, b) {
    const left = a || emptyUsage();
    const right = b || emptyUsage();
    const prompt = (Number(left.prompt_tokens) || 0) + (Number(right.prompt_tokens) || 0);
    const completion = (Number(left.completion_tokens) || 0) + (Number(right.completion_tokens) || 0);
    const cached = (Number(left.cached_tokens) || 0) + (Number(right.cached_tokens) || 0);
    const reasoning = (Number(left.reasoning_tokens) || 0) + (Number(right.reasoning_tokens) || 0);
    const cost = (Number(left.cost_usd) || 0) + (Number(right.cost_usd) || 0);
    const total = prompt + completion || (Number(left.total_tokens) || 0) + (Number(right.total_tokens) || 0);
    return {
        prompt_tokens: prompt,
        completion_tokens: completion,
        cached_tokens: cached,
        reasoning_tokens: reasoning,
        total_tokens: total,
        cost_usd: Number(cost.toFixed(8)),
        cost_usd_display: `$${cost.toFixed(6)}`,
        model: right.model || left.model || null,
    };
}

function formatSeconds(ms) {
    const sec = Math.max(0, Number(ms) || 0) / 1000;
    return Number(sec.toFixed(sec >= 10 ? 1 : 2));
}

async function chatTurn({ instituteId, conversationId, message, toolResults, signal }) {
    const body = {};
    if (conversationId) body.conversation_id = conversationId;
    if (message != null) body.message = message;
    if (toolResults) body.tool_results = toolResults;

    const response = await fetch(`${aiserverbaselink}/instowner/ai/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...aiInstituteHeaders(instituteId),
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '');
        throw new Error(`AI chat failed (${response.status}): ${text.slice(0, 200)}`);
    }

    let conversation_id = conversationId;
    let answer = '';
    let navigate = null;
    let error = null;
    let awaitingTools = false;
    let usage = null;
    const toolCalls = [];

    await consumeSse(
        response,
        ({ event, data }) => {
            if (event === 'meta' && data?.conversation_id) conversation_id = data.conversation_id;
            if (event === 'delta' && data?.content) answer += data.content;
            if (event === 'navigate') navigate = data;
            if (event === 'tools' && Array.isArray(data?.tool_calls)) {
                conversation_id = data.conversation_id || conversation_id;
                for (const call of data.tool_calls) {
                    toolCalls.push({
                        id: call.id,
                        name: call.name,
                        arguments:
                            typeof call.arguments === 'string'
                                ? safeJson(call.arguments)
                                : call.arguments || {},
                    });
                }
            }
            if (event === 'awaiting_tools') {
                awaitingTools = true;
                conversation_id = data?.conversation_id || conversation_id;
                // Prefer awaiting_tools usage (same blob as tools event — don't double-count)
                if (data?.usage) usage = data.usage;
            }
            if (event === 'done') {
                conversation_id = data?.conversation_id || conversation_id;
                if (data?.message) answer = data.message;
                navigate = data?.navigate || navigate;
                if (data?.usage) usage = data.usage;
            }
            if (event === 'error') {
                error = data?.error || data?.details || 'AI error';
            }
        },
        signal,
    );

    return {
        conversationId: conversation_id,
        answer: String(answer || '').trim(),
        navigate,
        error,
        awaitingTools,
        toolCalls,
        usage: usage || emptyUsage(),
    };
}

async function executeEvalTool(name, args, { allowWrites, signal }) {
    if (!allowWrites && MUTATING_TOOLS.has(name)) {
        return {
            ok: true,
            mocked: true,
            note: 'Write actions disabled for this eval run — tool was mocked.',
            tool: name,
        };
    }
    return executeDashboardTool(name, args);
}

async function runOneCase({ instituteId, testCase, allowWrites, maxRounds = 8, signal, onStatus }) {
    const toolsCalled = [];
    const toolPayloads = [];
    let usageAcc = emptyUsage();
    let last = await chatTurn({
        instituteId,
        conversationId: null,
        message: testCase.prompt,
        signal,
    });
    usageAcc = last.usage?.complete ? last.usage : mergeUsage(usageAcc, last.usage);
    let conversationId = last.conversationId;
    let rounds = 0;

    while (last.awaitingTools && last.toolCalls.length && rounds < maxRounds) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        rounds += 1;
        onStatus?.(`Running tools (${last.toolCalls.map((t) => t.name).join(', ')})…`);
        const toolResults = [];
        for (const call of last.toolCalls) {
            let args = call.arguments;
            if (call.name === 'get_customer_info') {
                const hasId = String(args?.customerid || args?.customer_id || args?.query || '').trim();
                if (!hasId) {
                    const analytics = [...toolPayloads].reverse().find((p) => p.name === 'get_pages_analytics');
                    const id =
                        analytics?.data?.top_customer?.customerid ||
                        analytics?.data?.top_customers?.[0]?.customerid ||
                        null;
                    if (id) args = { ...args, customerid: String(id) };
                }
            }
            toolsCalled.push({ name: call.name, arguments: args });
            let content;
            try {
                content = await executeEvalTool(call.name, args, { allowWrites, signal });
            } catch (err) {
                content = { ok: false, error: err.message || String(err) };
            }
            toolPayloads.push({ name: call.name, data: content });
            toolResults.push({
                tool_call_id: call.id,
                content: typeof content === 'string' ? content : JSON.stringify(content),
            });
        }
        last = await chatTurn({
            instituteId,
            conversationId,
            toolResults,
            signal,
        });
        usageAcc = last.usage?.complete ? last.usage : mergeUsage(usageAcc, last.usage);
        conversationId = last.conversationId || conversationId;
        if (last.error) break;
    }

    return {
        toolsCalled,
        toolPayloads,
        answer: last.answer,
        prompt: testCase.prompt,
        navigate: last.navigate,
        error: last.error,
        unfinished: Boolean(last.awaitingTools),
        conversationId,
        toolRounds: rounds,
        usage: usageAcc,
        fastPath: false,
    };
}

export async function fetchEvalSuite({ instituteId, suite = 'ask', stamp }) {
    const qs = new URLSearchParams({ suite, stamp: stamp || Date.now().toString(36) });
    const res = await fetch(`${aiserverbaselink}/instowner/ai/eval/suite?${qs}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...aiInstituteHeaders(instituteId),
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Failed to load suite (${res.status})`);
    return data;
}

export async function gradeEvalCase({ instituteId, caseId, result, stamp }) {
    const res = await fetch(`${aiserverbaselink}/instowner/ai/eval/grade`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...aiInstituteHeaders(instituteId),
        },
        body: JSON.stringify({
            case_id: caseId,
            stamp,
            result: {
                toolsCalled: result.toolsCalled,
                answer: result.answer,
                navigate: result.navigate,
                toolPayloads: result.toolPayloads || [],
            },
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Grade failed (${res.status})`);
    return data;
}

export async function saveEvalResults({ instituteId, payload }) {
    const res = await fetch(`${aiserverbaselink}/instowner/ai/eval/results`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...aiInstituteHeaders(instituteId),
        },
        body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Save failed (${res.status})`);
    return data;
}

/** List saved run collections (summaries). */
export async function listEvalRuns({ instituteId }) {
    const res = await fetch(`${aiserverbaselink}/instowner/ai/eval/results`, {
        method: 'GET',
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...aiInstituteHeaders(instituteId),
        },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Load failed (${res.status})`);
    return data;
}

/** Load one full run collection by id. */
export async function loadEvalRun({ instituteId, runId }) {
    const res = await fetch(
        `${aiserverbaselink}/instowner/ai/eval/results?id=${encodeURIComponent(runId)}`,
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                ...aiInstituteHeaders(instituteId),
            },
        },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Load failed (${res.status})`);
    return data.run || data;
}

export async function deleteEvalRun({ instituteId, runId }) {
    const res = await fetch(
        `${aiserverbaselink}/instowner/ai/eval/runs/${encodeURIComponent(runId)}`,
        {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                ...aiInstituteHeaders(instituteId),
            },
        },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Delete failed (${res.status})`);
    return data;
}

export async function renameEvalRun({ instituteId, runId, name }) {
    const res = await fetch(
        `${aiserverbaselink}/instowner/ai/eval/runs/${encodeURIComponent(runId)}`,
        {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                ...aiInstituteHeaders(instituteId),
            },
            body: JSON.stringify({ name }),
        },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Rename failed (${res.status})`);
    return data;
}

/** @deprecated use listEvalRuns */
export async function loadEvalResults({ instituteId }) {
    return listEvalRuns({ instituteId });
}

/**
 * Run the full suite in the browser (uses live Tabex session cookies for tools).
 */
export async function runTabexawyEval({
    instituteId,
    suite = 'ask',
    allowWrites = false,
    runName,
    signal,
    onProgress,
}) {
    const stamp = Date.now().toString(36);
    const suiteData = await fetchEvalSuite({ instituteId, suite, stamp });
    const cases = suiteData.cases || [];
    const results = [];

    for (let i = 0; i < cases.length; i += 1) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const testCase = cases[i];
        onProgress?.({
            index: i,
            total: cases.length,
            caseId: testCase.id,
            title: testCase.title,
            status: 'running',
        });

        const started = Date.now();
        let row;
        try {
            const raw = await runOneCase({
                instituteId,
                testCase,
                allowWrites,
                signal,
                onStatus: (text) =>
                    onProgress?.({
                        index: i,
                        total: cases.length,
                        caseId: testCase.id,
                        title: testCase.title,
                        status: 'running',
                        detail: text,
                    }),
            });

            if (raw.error) throw new Error(raw.error);
            if (raw.unfinished) throw new Error('Still awaiting tools after max rounds');

            const graded = await gradeEvalCase({
                instituteId,
                caseId: testCase.id,
                result: raw,
                stamp,
            });

            row = {
                id: testCase.id,
                title: testCase.title,
                suite: testCase.suite,
                mutating: Boolean(testCase.mutating),
                passed: Boolean(graded.passed),
                fail: graded.fail || null,
                ms: Date.now() - started,
                elapsed_seconds: formatSeconds(Date.now() - started),
                tools: (raw.toolsCalled || []).map((t) => t.name),
                toolFacts: raw.toolPayloads || [],
                prompt: testCase.prompt,
                answer: String(raw.answer || ''),
                answerPreview: String(raw.answer || '').slice(0, 240),
                usage: raw.usage || emptyUsage(),
                total_tokens: Number(raw.usage?.total_tokens) || 0,
                cost_usd: Number(raw.usage?.cost_usd) || 0,
                cost_usd_display: raw.usage?.cost_usd_display || '$0.000000',
                fastPath: Boolean(raw.fastPath),
            };
        } catch (err) {
            const ms = Date.now() - started;
            row = {
                id: testCase.id,
                title: testCase.title,
                suite: testCase.suite,
                mutating: Boolean(testCase.mutating),
                passed: false,
                fail: err.message || String(err),
                ms,
                elapsed_seconds: formatSeconds(ms),
                tools: [],
                prompt: testCase.prompt,
                answer: '',
                answerPreview: '',
                usage: emptyUsage(),
                total_tokens: 0,
                cost_usd: 0,
                cost_usd_display: '$0.000000',
            };
        }

        results.push(row);
        onProgress?.({
            index: i,
            total: cases.length,
            caseId: testCase.id,
            title: testCase.title,
            status: row.passed ? 'pass' : 'fail',
            row,
        });
    }

    const passed = results.filter((r) => r.passed).length;
    const totalTokens = results.reduce((sum, r) => sum + (Number(r.total_tokens) || 0), 0);
    const totalCost = results.reduce((sum, r) => sum + (Number(r.cost_usd) || 0), 0);
    const totalMs = results.reduce((sum, r) => sum + (Number(r.ms) || 0), 0);
    const finishedAt = new Date().toISOString();
    const summary = {
        passed,
        failed: results.length - passed,
        total: results.length,
        suite,
        allowWrites,
        finished_at: finishedAt,
        total_tokens: totalTokens,
        cost_usd: Number(totalCost.toFixed(8)),
        cost_usd_display: `$${totalCost.toFixed(6)}`,
        elapsed_ms: totalMs,
        elapsed_seconds: formatSeconds(totalMs),
    };

    let saved = null;
    try {
        saved = await saveEvalResults({
            instituteId,
            payload: {
                name: runName || undefined,
                suite,
                stamp,
                summary,
                cases: results,
            },
        });
    } catch (_) {
        /* non-fatal */
    }

    return {
        stamp,
        summary,
        cases: results,
        run_id: saved?.run_id || null,
        run: saved?.run || null,
        runs: saved?.runs || null,
    };
}
